import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import attendanceApi, { extraMachineApi, AttendanceEntry, ExtraMachineRequest } from '../services/attendanceApi';
import { useAuth } from '../contexts/AuthContext';
import ApprovalsTab from '../components/workforce/ApprovalsTab';
import SalaryTab from '../components/workforce/SalaryTab';
import PeopleTab from '../components/workforce/PeopleTab';
import EmployeeFormPanel from '../components/workforce/EmployeeFormPanel';
import {
  EmployeeOption, RoleOption, ModulePerm, CreateEmployeeForm, emptyCreateForm,
  anyPerm, ShiftLabel, nameOf, emName, fmt,
} from '../components/workforce/shared';

type Tab = 'approvals' | 'salary' | 'people';

const AttendanceApprovals: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Active section is driven by the URL so the sidebar submenu can deep-link into it
  // (/attendance-approvals, /attendance-approvals/salary, /attendance-approvals/people).
  const { tab: tabParam } = useParams<{ tab?: string }>();

  // Reject modals
  const [rejectEntry, setRejectEntry] = useState<AttendanceEntry | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectExtra, setRejectExtra] = useState<ExtraMachineRequest | null>(null);
  const [rejectExtraReason, setRejectExtraReason] = useState('');

  // Create / edit employee (full-page panel; editingId null = creating)
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [createForm, setCreateForm] = useState<CreateEmployeeForm>(emptyCreateForm);

  // Delete-employee confirm
  const [deletingEmployee, setDeletingEmployee] = useState<EmployeeOption | null>(null);

  const canApprove =
    user?.role === 'SUPERADMIN' || user?.role === 'ADMIN' || !!user?.employee?.canApproveAttendance;
  // Creating employees is restricted to admin-role users (not flagged managers).
  const isAdmin = user?.role === 'SUPERADMIN' || user?.role === 'ADMIN';

  // Map the URL segment to a section. Salary & People are admin-only; a non-admin who
  // lands on those URLs falls back to Approvals.
  const tab: Tab =
    isAdmin && tabParam === 'salary' ? 'salary'
    : isAdmin && tabParam === 'people' ? 'people'
    : 'approvals';

  // Normalize the URL: an unknown or unauthorized segment (e.g. a non-admin hitting
  // /salary, or a typo) is rewritten to the canonical path for the resolved section.
  useEffect(() => {
    const canonical = tab === 'approvals' ? undefined : tab;
    if (tabParam !== canonical) {
      navigate(tab === 'approvals' ? '/attendance-approvals' : `/attendance-approvals/${tab}`, { replace: true });
    }
  }, [tab, tabParam, navigate]);

  const { data: pendingData, isLoading: loadingPending } = useQuery({
    queryKey: ['attendance', 'pending'],
    queryFn: () => attendanceApi.getPending(),
    enabled: canApprove,
    refetchInterval: 15000,
  });

  const { data: pendingExtraData, isLoading: loadingPendingExtra } = useQuery({
    queryKey: ['extra-machines', 'pending'],
    queryFn: () => extraMachineApi.getPending(),
    enabled: canApprove,
    refetchInterval: 15000,
  });

  const { data: employeesData } = useQuery({
    queryKey: ['employees'],
    queryFn: () => apiService.get('/employees'),
    enabled: canApprove,
  });

  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: () => apiService.get('/roles'),
    enabled: canApprove && showCreate,
  });

  // Grantable modules for the Add Employee checklist. SUPERADMIN can grant everything;
  // an admin can only grant the modules they themselves have access to.
  const { data: modulesData } = useQuery({
    queryKey: ['admin-modules-all'],
    queryFn: () => apiService.get('/admin/modules/all'),
    enabled: user?.role === 'SUPERADMIN',
  });

  const pending: AttendanceEntry[] = pendingData?.entries ?? [];
  const pendingExtra: ExtraMachineRequest[] = pendingExtraData?.requests ?? [];
  const employees: EmployeeOption[] = employeesData?.employees ?? [];
  const roles: RoleOption[] = rolesData?.data ?? rolesData?.roles ?? [];
  const pendingCount = pending.length + pendingExtra.length;

  const grantableModules: any[] = React.useMemo(() => {
    if (!user) return [];
    if (user.role === 'SUPERADMIN') {
      const all = Array.isArray(modulesData) ? modulesData : ((modulesData as any)?.modules ?? (modulesData as any)?.data ?? []);
      return all.filter((m: any) => m && m.name !== 'company-management');
    }
    const perms = [...(((user as any).userRole?.permissions) ?? []), ...(((user as any).permissions) ?? [])];
    const mine = new Map<number, any>();
    perms.forEach((p: any) => {
      if (p?.canView && p?.module && p.module.name !== 'company-management') mine.set(p.module.id, p.module);
    });
    return Array.from(mine.values()).sort((a: any, b: any) => String(a.displayName || a.name).localeCompare(String(b.displayName || b.name)));
  }, [user, modulesData]);

  // ── Mutations ──────────────────────────────────────────────────────────────
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['attendance', 'pending'] });
    queryClient.invalidateQueries({ queryKey: ['attendance', 'records'] });
  };
  const invalidateExtra = () => {
    queryClient.invalidateQueries({ queryKey: ['extra-machines'] });
    queryClient.invalidateQueries({ queryKey: ['attendance', 'summary'] });
  };

  const approveMutation = useMutation({
    mutationFn: (id: number) => attendanceApi.approve(id),
    onSuccess: (res) => { toast.success(res.message || 'Approved'); invalidate(); },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to approve'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => attendanceApi.reject(id, reason),
    onSuccess: (res) => {
      toast.success(res.message || 'Rejected');
      setRejectEntry(null);
      setRejectReason('');
      invalidate();
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to reject'),
  });

  const approveExtraMutation = useMutation({
    mutationFn: (id: number) => extraMachineApi.approve(id),
    onSuccess: (res) => { toast.success(res.message || 'Approved'); invalidateExtra(); },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to approve'),
  });

  const rejectExtraMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => extraMachineApi.reject(id, reason),
    onSuccess: (res) => {
      toast.success(res.message || 'Rejected');
      setRejectExtra(null);
      setRejectExtraReason('');
      invalidateExtra();
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to reject'),
  });

  const saveEmployeeMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number | null; payload: any }) =>
      id ? apiService.put(`/employees/${id}`, payload) : apiService.post('/employees', payload),
    onSuccess: (_res, vars) => {
      toast.success(vars.id ? 'Employee updated' : 'Employee created');
      setShowCreate(false);
      setEditingId(null);
      setCreateForm(emptyCreateForm);
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to save employee'),
  });

  const deleteEmployeeMutation = useMutation({
    mutationFn: (id: number) => apiService.delete(`/employees/${id}`),
    onSuccess: () => {
      toast.success('Employee deleted');
      setDeletingEmployee(null);
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to delete employee'),
  });

  // Bulk helpers reuse the single mutations (fire one request per id).
  const approveMany = (ids: number[]) => ids.forEach((id) => approveMutation.mutate(id));
  const rejectMany = (ids: number[]) => ids.forEach((id) => rejectMutation.mutate({ id, reason: '' }));

  // ── Employee form open/submit ────────────────────────────────────────────────
  const openCreate = () => {
    setEditingId(null);
    setCreateForm(emptyCreateForm);
    setShowCreate(true);
  };

  const openEdit = (emp: EmployeeOption) => {
    setEditingId(emp.id);
    const modulePerms: Record<number, ModulePerm> = {};
    ((emp.user as any)?.permissions ?? []).forEach((p: any) => {
      if (!p?.moduleId) return;
      if (p.canView || p.canCreate || p.canUpdate || p.canDelete) {
        modulePerms[p.moduleId] = { read: !!p.canView, create: !!p.canCreate, edit: !!p.canUpdate, delete: !!p.canDelete };
      }
    });
    setCreateForm({
      name: emp.user?.name || '',
      email: emp.user?.email || '',
      phone: emp.user?.phone || '',
      password: '',
      designation: emp.designation || '',
      roleId: emp.user?.roleId != null ? String(emp.user.roleId) : '',
      canApproveAttendance: !!emp.canApproveAttendance,
      payType: emp.type === 'METER' ? 'METER' : 'FIXED',
      rate: emp.rate != null ? String(emp.rate) : '',
      shiftARate: emp.shiftARate != null ? String(emp.shiftARate) : '',
      shiftBRate: emp.shiftBRate != null ? String(emp.shiftBRate) : '',
      extraUnit: emp.extraUnit === 'HOUR' ? 'HOUR' : 'MACHINE',
      extraRate: emp.extraRate != null ? String(emp.extraRate) : '',
      modulePerms,
    });
    setShowCreate(true);
  };

  const submitEmployee = () => {
    if (!createForm.name.trim() || !createForm.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    if (!editingId && !createForm.password.trim()) {
      toast.error('Password is required');
      return;
    }
    const permissions = Object.entries(createForm.modulePerms)
      .filter(([, perm]) => anyPerm(perm))
      .map(([moduleId, perm]) => ({
        moduleId: Number(moduleId),
        canView: perm.read,
        canCreate: perm.create,
        canUpdate: perm.edit,
        canDelete: perm.delete,
      }));
    const payload: any = {
      name: createForm.name.trim(),
      email: createForm.email.trim(),
      phone: createForm.phone.trim() || null,
      roleId: createForm.roleId ? Number(createForm.roleId) : null,
      designation: createForm.designation.trim() || null,
      canApproveAttendance: createForm.canApproveAttendance,
      type: createForm.payType,
      rate: createForm.rate.trim() === '' ? 0 : Number(createForm.rate) || 0,
      shiftARate: createForm.payType === 'METER' && createForm.shiftARate.trim() !== '' ? Number(createForm.shiftARate) || 0 : null,
      shiftBRate: createForm.payType === 'METER' && createForm.shiftBRate.trim() !== '' ? Number(createForm.shiftBRate) || 0 : null,
      extraUnit: createForm.extraUnit,
      extraRate: createForm.extraRate.trim() === '' ? 0 : Number(createForm.extraRate) || 0,
      permissions,
    };
    if (!editingId) payload.password = createForm.password;
    saveEmployeeMutation.mutate({ id: editingId, payload });
  };
  // roles kept available for future role-select wiring in the form panel.
  void roles;

  if (!canApprove) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Workforce</h1>
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4">
          You do not have permission to approve attendance. Ask an admin to enable
          "Can approve attendance" on your employee profile.
        </div>
      </div>
    );
  }

  // Full-page employee create/edit view.
  if (showCreate && isAdmin) {
    return (
      <EmployeeFormPanel
        form={createForm}
        setForm={setCreateForm}
        editingId={editingId}
        grantableModules={grantableModules}
        saving={saveEmployeeMutation.isPending}
        onCancel={() => setShowCreate(false)}
        onSubmit={submitEmployee}
      />
    );
  }

  // The section is chosen from the sidebar submenu now; the page just shows a heading
  // for the active section (title + optional count) plus the Add Employee action.
  const section: { title: string; subtitle: string; badge?: number } =
    tab === 'salary' ? { title: 'Salary', subtitle: 'Monthly payable and attendance per karigar' }
    : tab === 'people' ? { title: 'People', subtitle: 'Everyone on the books, grouped by role', badge: employees.length }
    : { title: 'Approvals', subtitle: 'Clock-ins and extra-machine runs awaiting review', badge: pendingCount };

  return (
    <div className="p-4 sm:p-6 w-full">
      {/* Section heading + actions */}
      <div className="flex items-start justify-between gap-3 mb-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-gray-900">{section.title}</h1>
            {section.badge != null && section.badge > 0 && (
              <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium ${tab === 'approvals' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'}`}>
                {section.badge}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-0.5">{section.subtitle}</p>
        </div>
        {isAdmin && (
          <button
            onClick={openCreate}
            className="shrink-0 mb-1.5 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
          >
            <UserPlus className="h-4 w-4" /> Add Employee
          </button>
        )}
      </div>

      {tab === 'approvals' ? (
        <ApprovalsTab
          pending={pending}
          pendingExtra={pendingExtra}
          employees={employees}
          loadingPending={loadingPending}
          loadingPendingExtra={loadingPendingExtra}
          busy={approveMutation.isPending || rejectMutation.isPending || approveExtraMutation.isPending}
          onApprove={(id) => approveMutation.mutate(id)}
          onReject={(entry) => { setRejectEntry(entry); setRejectReason(''); }}
          onApproveMany={approveMany}
          onRejectMany={rejectMany}
          onApproveExtra={(id) => approveExtraMutation.mutate(id)}
          onRejectExtra={(req) => { setRejectExtra(req); setRejectExtraReason(''); }}
        />
      ) : tab === 'salary' ? (
        <SalaryTab employees={employees} />
      ) : (
        <PeopleTab
          employees={employees}
          isAdmin={isAdmin}
          onEdit={openEdit}
          onDelete={(emp) => setDeletingEmployee(emp)}
        />
      )}

      {/* Delete employee confirm */}
      {deletingEmployee && isAdmin && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Delete employee</h3>
            <p className="text-sm text-gray-600 mb-5">
              Delete <span className="font-medium">{deletingEmployee.user?.name || `Employee #${deletingEmployee.id}`}</span> and their login?
              This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeletingEmployee(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Cancel</button>
              <button
                onClick={() => deleteEmployeeMutation.mutate(deletingEmployee.id)}
                disabled={deleteEmployeeMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleteEmployeeMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject clock-in modal */}
      {rejectEntry && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Reject clock-in</h3>
            <p className="text-sm text-gray-500 mb-4">
              {nameOf(rejectEntry)} · <ShiftLabel shift={rejectEntry.shiftType} /> · {fmt(rejectEntry.clockInAt)}
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-2">Reason (optional)</label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g. Not scheduled for this shift"
            />
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setRejectEntry(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Cancel</button>
              <button
                onClick={() => rejectMutation.mutate({ id: rejectEntry.id, reason: rejectReason })}
                disabled={rejectMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {rejectMutation.isPending ? 'Rejecting…' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject extra-machine request modal */}
      {rejectExtra && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Reject extra-machine request</h3>
            <p className="text-sm text-gray-500 mb-4">
              {emName(rejectExtra)} · {dayjs(rejectExtra.workDate).format('DD MMM YYYY')} · {rejectExtra.quantity} {rejectExtra.unit === 'HOUR' ? 'hour' : 'machine'}{rejectExtra.quantity === 1 ? '' : 's'}
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-2">Reason (optional)</label>
            <textarea
              value={rejectExtraReason}
              onChange={(e) => setRejectExtraReason(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g. Machines were already counted"
            />
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setRejectExtra(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Cancel</button>
              <button
                onClick={() => rejectExtraMutation.mutate({ id: rejectExtra.id, reason: rejectExtraReason })}
                disabled={rejectExtraMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {rejectExtraMutation.isPending ? 'Rejecting…' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceApprovals;
